import React, { useEffect, useRef, useState } from "react";
import * as Sentry from "@sentry/nextjs";
import { useTranslation } from 'next-i18next';
import ErrorMessage from "./ErrorMessage/ErrorMessage";
import IsValidName from "../../../utils/validators/IsValidName";
import IsValidEmail from "../../../utils/validators/IsValidEmail";
import { Formik } from "formik";
import ReCAPTCHA from "react-google-recaptcha";
import { ApiService } from "../../../services/ApiRequests";
import styles from "./ContactUs.module.scss"
import Swal from "sweetalert2";
import useOnScreen from "../../../hooks/useOnScreen";
import axios from "axios";

const WHATSAPP_DEFAULT_MESSAGE = 'Olá! Gostaria de mais informações sobre como a Verzel pode me ajudar.';

const ContactUs = () => {
    const api = new ApiService();
    const { t } = useTranslation();
    const [tokenReCaptcha, setToken] = useState("");
    const [refreshReCaptcha, setRefreshReCaptcha] = useState(false);
    const reCaptchaRef = useRef();
    const [windowWidth, setWindowWidth] = useState(null);

    const refContainer = useRef(null)
    const contactUsSectionIsVisible = useOnScreen(refContainer)

    useEffect(() => {
        if (!windowWidth) {
            setWindowWidth(window.innerWidth);
        }
    }, [windowWidth]);

    useEffect(() => {
        function handleResize() {
            setWindowWidth(window.innerWidth);
        }

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const helpOptions = {
        "developIdea": "Preciso desenvolver uma ideia",
        "understandDigitalNeeds": "Preciso de ajuda para entender o que digitalmente preciso",
        "manageSystems": "Preciso de um parceiro para cuidar dos meus sistemas",
        "optimizeProcesses": "Gostaria de otimizar processos ou aumentar receita"
    }

    // useEffect(() => {
    //     if (refreshReCaptcha) {
    //         reCaptchaRef.current.reset();
    //         setToken("");
    //         setRefreshReCaptcha(false);
    //     }
    // }, [refreshReCaptcha]);

    const executeReCaptcha = async () => {
        const token = await reCaptchaRef.current.executeAsync();
        setToken(token);
        return token;
    };

    const handleSubmit = async (values, resetForm) => {
        try {

            // TODO: verify the token (backend)
            const token = undefined;
            if (!tokenReCaptcha) {
                token = await executeReCaptcha();
                if (!token) {
                    console.error("ReCaptcha token not generated");
                    return;
                }
            }

            const payload = {
                name: values.name,
                email: values.email,
                subject: values.helpType,
                token
            }

            await axios.post(`${process.env.NEXT_PUBLIC_VERZEL_FUNCTIONS_HOST}/contact-us`, payload);

            Swal.fire({
                icon: "success",
                title: t("contactUs.contactSuccess"),
                showConfirmButton: false,
                timer: 2500,
            })

            resetForm({
                name: "",
                email: "",
                helpType: ""
            })

        } catch (error) {
            console.error(error)
            Sentry.captureException(error, {
                tags: { form: 'contact-us-landing' },
                extra: { 
                    apiUrl: `${process.env.NEXT_PUBLIC_VERZEL_FUNCTIONS_HOST}/contact-us`,
                    formData: { name: values.name, email: values.email, subject: values.helpType }
                }
            });
            Swal.fire({
                icon: "error",
                title: t("contactUs.error.send"),
                showConfirmButton: false,
                timer: 2500,
            })
        } finally {
            setRefreshReCaptcha(true)
        }
    }

    const validate = (values) => {
        const errors = {};
        if (!values.name) {
            errors.name = t("contactUs.error.mandatoryFilling");
        } else if (!IsValidName(values.name)) {
            errors.name = t("contactUs.error.invalidName");
        }
        if (!values.email) {
            errors.email = t("contactUs.error.mandatoryFilling");
        } else if (!IsValidEmail(values.email)) {
            errors.email = t("contactUs.error.invalidEmail");
        }
        if (!values.helpType) {
            errors.helpType = t("contactUs.error.mandatoryFilling");
        }
        return errors;
    }

    return (
        <div className={styles.container_wrapper} id="contact-us" ref={refContainer}>
            <div className={styles.gradient} />
            <Formik
                initialValues={{
                    name: "",
                    email: "",
                    helpType: ""
                }}
                onSubmit={async (values, { resetForm }) => {
                    await handleSubmit(values, resetForm)
                }}
                validate={validate}
            >
                {(formik) => (
                    <div className={styles.container}>
                        <form onSubmit={(e) => {
                            e.preventDefault();
                            formik.handleSubmit();
                        }}>
                            <div className={styles.line}></div>

                            <p className={styles.title}>
                                {t("contactUs.title")}
                            </p>
                            <p className={styles.text}>
                                {t("contactUs.text1")}
                                {windowWidth < 992 ? " " : <br />}
                                {t("contactUs.text2")}
                            </p>

                            <div className={styles.input_field}>
                                <label>{t("contactUs.inputField.name")}</label>
                                <input
                                    type="text"
                                    id="name"
                                    name="name"
                                    placeholder={t("contactUs.inputField.write")}
                                    value={formik.values.name}
                                    style={{
                                        borderColor: formik.touched.name && formik.errors.name ? "#FF3938" : "white",
                                        borderWidth: formik.touched.name && formik.errors.name ? "1.5px" : undefined
                                    }}
                                    onBlur={formik.handleBlur}
                                    onChange={formik.handleChange}
                                />
                                {(formik.touched.name && formik.errors.name) &&
                                    <ErrorMessage
                                        name="name"
                                        component="div"
                                        style={{ color: "red" }}
                                        message={formik.errors.name}
                                    />}
                            </div>

                            <div className={styles.input_field}>
                                <label>{t("contactUs.inputField.email")}</label>
                                <input
                                    type="text"
                                    id="email"
                                    name="email"
                                    placeholder={t("contactUs.inputField.write")}
                                    value={formik.values.email}
                                    style={{
                                        borderColor: formik.touched.email && formik.errors.email ? "#FF3938" : "white",
                                        borderWidth: formik.touched.email && formik.errors.email ? "1.5px" : undefined
                                    }}
                                    onBlur={formik.handleBlur}
                                    onChange={formik.handleChange}
                                />
                                {(formik.touched.email && formik.errors.email) &&
                                    <ErrorMessage
                                        name="email"
                                        component="div"
                                        style={{ color: "red" }}
                                        message={formik.errors.email}
                                    />}
                            </div>
                            <div className={styles.input_field}>
                                <label>{t("contactUs.inputField.help")}</label>
                                <select
                                    placeholder={t("contactUs.select")}
                                    id="helpType"
                                    name="helpType"
                                    style={{
                                        borderColor: formik.touched.helpType && formik.errors.helpType ? "#FF3938" : "white",
                                        borderWidth: formik.touched.helpType && formik.errors.helpType ? "1.5px" : undefined
                                    }}
                                    value={formik.values.helpType}
                                    onBlur={formik.handleBlur}
                                    onChange={formik.handleChange}
                                >
                                    <option value="" hidden>{t("contactUs.select")}</option>
                                    {Object.keys(helpOptions).map((key) => {
                                        return (
                                            <option
                                                key={key}
                                                value={helpOptions[key]}>{t(`contactUs.helpOptions.${key}`)}
                                            </option>
                                        )
                                    })}
                                </select>
                                {(formik.touched.helpType && formik.errors.helpType) &&
                                    <ErrorMessage
                                        name="helpType"
                                        component="div"
                                        style={{ color: "red" }}
                                        message={formik.errors.helpType}
                                    />}
                            </div>

                            <p className={styles.privacy_consent}>
                                Ao enviar, você concorda com nossa{" "}
                                <a href="/pt/privacy-policy" target="_blank" rel="noreferrer">
                                    <u>Política de Privacidade</u>
                                </a>.
                            </p>

                            <div className={styles.form_buttons_wrapper}>
                                <button className={styles.button} type="submit">
                                    <span className={styles.button_text}>
                                        {t("contactUs.inputField.send")}
                                    </span>
                                </button>
                                <button
                                    className={`${styles.button} ${styles.whatsapp_button}`}
                                    onClick={() => {
                                        window.open(
                                            `https://wa.me/${process.env.NEXT_PUBLIC_WHATSAPP}?text=${WHATSAPP_DEFAULT_MESSAGE}`,
                                            "_blank",
                                        );
                                    }}
                                >
                                    <img src="/landings/assets/icons/whatsapp.svg" alt="Logo do WhatsApp" />
                                    <span className={styles.button_text}>
                                        {t("contactUs.startChat")}
                                    </span>
                                </button>
                            </div>
                            {/* <div className={styles.other_countries}>
                                {windowWidth > 992 ? <p>{t("contactUs.phones.text1")} <br />{t("contactUs.phones.text2")} <br />{t("contactUs.phones.text3")}</p>
                                : <p>{t("contactUs.phones.textMobile")}</p>}
                                <div className={styles.countries_group}>
                                    <div className={styles.country}>
                                        <img src="/landings/assets/icons/flag-brazil.svg"></img>
                                        <span className={styles.country_label}>São Paulo, BR</span>
                                        <span className={styles.country_number}>0800 000 0000</span>
                                    </div>
                                    <div className={styles.country_separator} />
                                    <div className={styles.country}>
                                        <img src="/landings/assets/icons/flag-brazil.svg"></img>
                                        <span className={styles.country_label}>Rio de janeiro, BR</span>
                                        <span className={styles.country_number}>0800 000 0000</span>
                                    </div>
                                    <div className={styles.country_separator} />
                                    <div className={styles.country}>
                                        <img src="/landings/assets/icons/flag-portugal.svg"></img>
                                        <span className={styles.country_label}>Lisboa, PT</span>
                                        <span className={styles.country_number}>+351 0000 0000</span>
                                    </div>
                                    <div className={styles.country_separator} />
                                    <div className={styles.country}>
                                        <img src="/landings/assets/icons/flag-germany.svg"></img>
                                        <span className={styles.country_label}>Berlim, DE</span>
                                        <span className={styles.country_number}>+49 0000 0000</span>
                                    </div>
                                </div>
                            </div> */}
                            <ReCAPTCHA
                                sitekey={process.env.NEXT_PUBLIC_APP_SITE_KEY_RECAPTCHA}
                                size="invisible"
                                ref={reCaptchaRef}
                                badge="bottomleft"
                                onErrored={() => {
                                    console.log('captcha > error')
                                    setRefreshReCaptcha(true)
                                }}
                                onExpired={() => {
                                    console.log('captcha > expired')
                                    setRefreshReCaptcha(true)
                                }}
                            />
                        </form>
                    </div>
                )}
            </Formik>
            <button hidden={contactUsSectionIsVisible} className={styles.whatsapp_floating_button} onClick={() => {
                window.open(
                    `https://wa.me/${process.env.NEXT_PUBLIC_WHATSAPP}?text=${WHATSAPP_DEFAULT_MESSAGE}`,
                    "_blank",
                );
            }}>
                {windowWidth > 992
                    ?
                    <img src="/landings/assets/icons/whatsapp.svg" alt="Logo do WhatsApp" />
                    :
                    <img src="/landings/assets/icons/whatsapp.svg" alt="Logo do WhatsApp" />
                }

                {windowWidth > 992 && <span>{t("contactUs.startChat")}</span>}
            </button>
            <style>
                {`
                    .swal2-modal {
                        background-color: #1D202A
                    }

                    .swal2-title {
                        color: #fff; /* Cor do título */
                        font-size: 1.8vw; /* Tamanho da fonte do título */
                    }
                `}
            </style>
        </div>
    )
}

export default ContactUs;